"use client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { useState, startTransition } from "react";
import { Button } from "@/components/ui/button";
import { RINGBA_DNI_PHONE, formatPhoneNumber } from "@/lib/constants";
import { navigateWithQueryParams } from "@/utils/navigationUtils";
import GcsImage from "@/components/common/GcsImage";
import MobileHeaderCallButton from "@/components/common/MobileHeaderCallButton";
import { isMobileHeaderCallLandingPage } from "@/utils/mobileLandingPages";
import { isCarrierHomeLandingPath } from "@/data/carrierHomeLandings";
import { getFirstLandingPage } from "@/utils/firstLandingPage";
import {
  isMmmCallForwardLandingPage,
  isMmmCampaignCompareThankYou,
  isMmmZipLandingPage,
} from "@/utils/mmmCampaignLanding";
import CallButton from "@/components/call-forward/CallButton";

const Navigation = () => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [insuranceDropdownOpen, setInsuranceDropdownOpen] = useState(false);
  const [careersDropdownOpen, setCareersDropdownOpen] = useState(false);

  const aboutSubPages = [
    { name: "Get To Know Us", path: "/about/get-to-know-us" },
    { name: "How It Works", path: "/about/how-it-works" },
    { name: "Meet Our Leaders", path: "/about/leaders" },
    { name: "Reviews", path: "/about/reviews" },
    { name: "Where We Serve", path: "/about/where-we-serve" },
    { name: "FAQs", path: "/about/faqs" },
  ];

  const insuranceSubPages = [
    { name: "Home Insurance", path: "/insurance/home" },
    { name: "Auto Insurance", path: "/insurance/auto" },
    { name: "Specialty Insurance", path: "/insurance/specialty" },
    { name: "Umbrella Insurance", path: "/insurance/umbrella" },
    { name: "Jewelry Insurance", path: "/insurance/jewelry" },
    { name: "Our Carriers", path: "/insurance/carriers" },
    { name: "Utility Line Warranty", path: "/insurance/utility-line-warranty" },
  ];

  const careersSubPages = [
    { name: "Life at Guardian Service", path: "/careers/life" },
    { name: "Meet Our Agents", path: "/careers/agents" },
    { name: "Join Us", path: "/careers/join" },
  ];

  function isActive(path: string) {
    return pathname === path;
  }
  const isAboutActive = pathname.startsWith("/about");
  const isInsuranceActive = pathname.startsWith("/insurance");
  const isCareersActive = pathname.startsWith("/careers");
  const isCompareActive = pathname.startsWith("/compare") || pathname === "/auto-compare";
  const isShopRatesActive = pathname.startsWith("/shop-rates");
  const isShopRatesBActive =
    (pathname ?? "").replace(/\/$/, "") === "/shop-rates-b";
  const headerComparePath = isShopRatesBActive ? "/compare-2" : "/compare";
  const headerCompareQuery = isShopRatesBActive
    ? { step: "1" }
    : undefined;
  const isTake5Active = pathname.startsWith("/take5");
  const isMail121PageActive = pathname.startsWith("/121");
  const isZipHeroLanding = isShopRatesActive || isMail121PageActive;
  const isGnewsActive = pathname.startsWith("/gnews");
  const isCallVariantActive = isMmmZipLandingPage(pathname ?? "");
  const isZipVariantActive = isMmmCallForwardLandingPage(pathname ?? "");
  const isCampaignThankYou = isMmmCampaignCompareThankYou(
    pathname ?? "",
    searchParams.get("step"),
    getFirstLandingPage(),
  );
  const isOpayActive = pathname.startsWith("/opay");
  const isCompare2Active = pathname.startsWith("/compare-2");
  const isCheapActive =
    pathname.startsWith("/cheap-home-insurance") ||
    pathname.startsWith("/cheap-landlord-insurance") ||
    pathname.startsWith("/home-insurance-reviews") ||
    isCarrierHomeLandingPath(pathname ?? "");
  const isWhcActive = pathname.startsWith("/whc");
  const isInviteActive = pathname.startsWith("/invite");

  const navLinks = [
    {
      name: "About Us",
      path: "/about/get-to-know-us",
      hasDropdown: true,
      dropdownType: "about",
    },
    {
      name: "Insurance",
      path: "/insurance/home",
      hasDropdown: true,
      dropdownType: "insurance",
    },
    { name: "Partnerships", path: "/partnerships" },
    {
      name: "Careers",
      path: "/careers/life",
      hasDropdown: true,
      dropdownType: "careers",
    },
    { name: "Blog", path: "/blog" },
    {
      name: "Contact",
      path: "/contact",
    },
  ];

  if (isCheapActive) return null;
  if (isCompare2Active) return null;
  if (isWhcActive) return null;

  if (isCampaignThankYou) {
    return (
      <nav className="bg-background border-b border-border sticky top-0 z-50">
        <div className="mx-auto px-4">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigateWithQueryParams(router, "/")}
              className="flex items-center relative h-10 md:h-12 cursor-pointer"
            >
              <GcsImage
                path="icons/Blue-Stacked.png"
                alt="Guardian Service"
                className="h-full w-auto lg:hidden object-contain"
                priority
              />
              <GcsImage
                path="icons/Blue-OneLine.png"
                alt="Guardian Service"
                className="hidden lg:block h-full w-auto object-contain"
                priority
              />
            </button>
          </div>
        </div>
      </nav>
    );
  }

  const shouldHideMenu = () =>
    isGnewsActive ||
    isCallVariantActive ||
    isZipVariantActive ||
    isOpayActive ||
    isShopRatesActive ||
    isTake5Active ||
    isMail121PageActive ||
    isCompareActive ||
    isInviteActive;

  const shouldShowMobileCallIcon = isMobileHeaderCallLandingPage(
    pathname ?? "",
  );

  const toggleMobileMenu = () => {
    startTransition(() => {
      setMobileMenuOpen((open) => !open);
    });
  };

  return (
    <nav className="bg-background border-b border-border sticky top-0 z-50">
      <div
        className={`mx-auto px-4${isZipHeroLanding ? " min-[1600px]:max-w-[1400px]" : ""}`}
      >
        <div className="flex items-center justify-between h-16">
          {/* Logo - Reserve space for both to prevent layout shift */}
          <button
            onClick={() => navigateWithQueryParams(router, "/")}
            className="flex items-center relative h-10 md:h-12 cursor-pointer"
          >
            <GcsImage
              path="icons/Blue-Stacked.png"
              alt="Guardian Service"
              className="h-full w-auto lg:hidden object-contain"
              priority
            />
            <GcsImage
              path="icons/Blue-OneLine.png"
              alt="Guardian Service"
              className="hidden lg:block h-full w-auto object-contain"
              priority
            />
          </button>

          {isCallVariantActive ? (
            <CallButton
              showCallPrefix={false}
              className="h-10 px-3 sm:px-4 text-xs sm:text-sm w-auto"
              iconClassName="h-4 w-4"
            />
          ) : (
            <>
              {/* Tablet/Mobile CTA */}
              <div className="flex lg:hidden items-center gap-2">
                {isZipVariantActive ? (
                  <CallButton
                    showCallPrefix={false}
                    phoneNumber={RINGBA_DNI_PHONE}
                    className="h-10 px-3 text-xs w-auto"
                    iconClassName="h-4 w-4"
                  />
                ) : (
                  <>
                    {shouldShowMobileCallIcon && <MobileHeaderCallButton />}
                    <a
                      href={`tel:${RINGBA_DNI_PHONE}`}
                      className="hidden md:flex lg:hidden items-center gap-1 text-xs font-medium text-[#090739] hover:text-[#FFB703] transition-colors min-w-27.5"
                      suppressHydrationWarning={true} // Ringba's Number Insertion changes the phone number client-side
                    >
                      <Phone className="h-3 w-3 shrink-0" />
                      <span
                        className="tabular-nums"
                        suppressHydrationWarning={true}
                      >
                        {formatPhoneNumber(RINGBA_DNI_PHONE)}
                      </span>
                    </a>

                    {!isCompareActive && (
                      <Button
                        variant={"compare"}
                        onClick={() =>
                          navigateWithQueryParams(
                            router,
                            headerComparePath,
                            headerCompareQuery,
                          )
                        }
                      >
                        COMPARE QUOTES
                      </Button>
                    )}
                  </>
                )}
                {/* Mobile Menu Button */}
                {!isCompareActive && !shouldHideMenu() && (
                  <button
                    className="p-2 cursor-pointer"
                    onClick={toggleMobileMenu}
                    aria-label="Toggle menu"
                    aria-expanded={mobileMenuOpen}
                  >
                    {mobileMenuOpen ? (
                      <X className="h-6 w-6" />
                    ) : (
                      <Menu className="h-6 w-6" />
                    )}
                  </button>
                )}
              </div>

              {/* Desktop Navigation */}

              <div className="hidden lg:flex items-center gap-6">
                {!shouldHideMenu() &&
                  navLinks.map((link) =>
                    link.hasDropdown ? (
                      <div
                        key={link.path}
                        className="relative"
                        onMouseEnter={() => {
                          if (link.dropdownType === "about")
                            setAboutDropdownOpen(true);
                          else if (link.dropdownType === "insurance")
                            setInsuranceDropdownOpen(true);
                          else if (link.dropdownType === "careers")
                            setCareersDropdownOpen(true);
                        }}
                        onMouseLeave={() => {
                          if (link.dropdownType === "about")
                            setAboutDropdownOpen(false);
                          else if (link.dropdownType === "insurance")
                            setInsuranceDropdownOpen(false);
                          else if (link.dropdownType === "careers")
                            setCareersDropdownOpen(false);
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            navigateWithQueryParams(router, link.path)
                          }
                          className={`text-sm font-medium transition-colors hover:text-[#FFB703] flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 ${
                            (link.dropdownType === "about" && isAboutActive) ||
                            (link.dropdownType === "insurance" &&
                              isInsuranceActive) ||
                            (link.dropdownType === "careers" && isCareersActive)
                              ? "text-primary border-b-2 border-primary"
                              : "text-muted-foreground"
                          }`}
                        >
                          {link.name}
                          <ChevronDown className="h-4 w-4" />
                        </button>

                        {((link.dropdownType === "about" &&
                          aboutDropdownOpen) ||
                          (link.dropdownType === "insurance" &&
                            insuranceDropdownOpen) ||
                          (link.dropdownType === "careers" &&
                            careersDropdownOpen)) && (
                          <div className="absolute top-full left-0 pt-2 bg-background border border-border rounded-md shadow-lg min-w-50 pb-2 z-50">
                            {(link.dropdownType === "about"
                              ? aboutSubPages
                              : link.dropdownType === "insurance"
                                ? insuranceSubPages
                                : careersSubPages
                            ).map((subPage) => (
                              <button
                                key={subPage.path}
                                onClick={() =>
                                  navigateWithQueryParams(router, subPage.path)
                                }
                                className="block w-full text-left px-4 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-[#090739] cursor-pointer"
                              >
                                {subPage.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        key={link.path}
                        onClick={() =>
                          navigateWithQueryParams(router, link.path)
                        }
                        className={`text-sm font-medium transition-colors hover:text-[#FFB703] cursor-pointer ${
                          isActive(link.path)
                            ? "text-primary border-b-2 border-primary"
                            : "text-muted-foreground"
                        }`}
                      >
                        {link.name}
                      </button>
                    ),
                  )}
                {!isCheapActive && (
                  <a
                    href={`tel:${RINGBA_DNI_PHONE}`}
                    className="flex items-center gap-2 text-sm font-medium text-[#090739] hover:text-[#FFB703] transition-colors min-w-35"
                    suppressHydrationWarning={true} // Ringba's Number Insertion changes the phone number client-side
                  >
                    <Phone className="h-4 w-4 shrink-0" />
                    <span
                      className="tabular-nums"
                      suppressHydrationWarning={true}
                    >
                      {formatPhoneNumber(RINGBA_DNI_PHONE)}
                    </span>
                  </a>
                )}
                {!isCompareActive && !isTake5Active && (
                  <Button
                    variant={"compare"}
                    onClick={() => {
                      navigateWithQueryParams(
                        router,
                        headerComparePath,
                        headerCompareQuery,
                      );
                      setMobileMenuOpen(false);
                    }}
                  >
                    COMPARE QUOTES
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            {navLinks.map((link) => (
              <div key={link.path}>
                {link.hasDropdown ? (
                  <button
                    type="button"
                    onClick={() => {
                      navigateWithQueryParams(router, link.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`block w-full text-left py-2 px-4 text-sm font-medium transition-colors cursor-pointer bg-transparent border-0 ${
                      (link.dropdownType === "about" && isAboutActive) ||
                      (link.dropdownType === "insurance" &&
                        isInsuranceActive) ||
                      (link.dropdownType === "careers" && isCareersActive)
                        ? "text-[#090739] bg-[#FFE9B4]!"
                        : "text-muted-foreground"
                    }`}
                  >
                    {link.name}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      navigateWithQueryParams(router, link.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`block w-full text-left py-2 px-4 text-sm font-medium transition-colors hover:bg-accent cursor-pointer ${
                      isActive(link.path)
                        ? "text-[#090739] bg-accent"
                        : "text-muted-foreground"
                    }`}
                  >
                    {link.name}
                  </button>
                )}
                {link.hasDropdown && (
                  <div className="pl-4">
                    {(link.dropdownType === "about"
                      ? aboutSubPages
                      : link.dropdownType === "insurance"
                        ? insuranceSubPages
                        : careersSubPages
                    ).map((subPage) => (
                      <button
                        key={subPage.path}
                        onClick={() => {
                          navigateWithQueryParams(router, subPage.path);
                          setMobileMenuOpen(false);
                        }}
                        className={`block w-full text-left py-2 px-4 text-sm transition-colors hover:bg-accent cursor-pointer ${
                          isActive(subPage.path)
                            ? "text-[#090739] font-bold bg-accent"
                            : "text-muted-foreground"
                        }`}
                      >
                        {subPage.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="px-4 pt-4 space-y-3">
              <a
                href={`tel:${RINGBA_DNI_PHONE}`}
                className="flex items-center justify-center gap-2 text-sm font-medium text-[#090739] hover:text-[#FFB703] transition-colors"
                suppressHydrationWarning={true} // Ringba's Number Insertion changes the phone number client-side
              >
                <Phone className="h-4 w-4" />
                <span suppressHydrationWarning={true}>
                  {formatPhoneNumber(RINGBA_DNI_PHONE)}
                </span>
              </a>
              {!isCompareActive && (
                <Button
                  variant={"compare"}
                  onClick={() => {
                    navigateWithQueryParams(
                      router,
                      headerComparePath,
                      headerCompareQuery,
                    );
                    setMobileMenuOpen(false);
                  }}
                >
                  COMPARE QUOTES
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
